import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { PageHeader } from './page-header';

interface FormPageProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  onBack?: () => void;
  /** The back button's accessible name; defaults to "Go back". */
  backLabel?: string;
  /** Shown beside the title, for a page people act on rather than fill in: what
   * can be done with a record belongs where it is named, not below it. */
  actions?: ReactNode;
  children: ReactNode;
  /** A right-hand rail beside the form, e.g. context that helps fill it in.
   * Stacks below the form under 1024px. Omit for a single-column page — the
   * existing create/edit pages don't pass this and are unaffected. */
  aside?: ReactNode;
  /**
   * Pins these actions under the page header, and to the top of the viewport
   * once the form scrolls, instead of letting them scroll away with a long
   * form. Opt-in: a page that omits this keeps its buttons in a FormCard
   * footer as before.
   */
  stickyActions?: ReactNode;
}

/** Page shell for create/edit flows — a wide container so the form cards span
 * the content area, with an icon header at the top. */
export function FormPage({
  title,
  description,
  icon,
  onBack,
  backLabel,
  actions,
  children,
  aside,
  stickyActions,
}: FormPageProps) {
  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-6">
      <PageHeader
        title={title}
        description={description}
        icon={icon}
        onBack={onBack}
        backLabel={backLabel}
        actions={actions}
      />

      {stickyActions ? (
        // Pinned under the page header, then to the top edge once the form
        // scrolls: the negative top offset cancels the shell's py-7, so it
        // docks flush instead of floating with the form showing above it.
        <div className="sticky -top-7 z-20 flex min-h-14 flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card px-5 py-3 shadow-[0_6px_16px_-10px_rgb(0_0_0/0.18)]">
          {stickyActions}
        </div>
      ) : null}
      <div
        className={cn('animate-fade-in flex flex-col gap-6', aside && 'lg:flex-row lg:items-start')}
      >
        <div className="min-w-0 flex-1">{children}</div>
        {aside ? (
          // With pinned actions the rail sticks below them, not underneath them.
          <div
            className={cn(
              'w-full shrink-0 lg:sticky lg:w-[360px]',
              stickyActions ? 'lg:top-12' : 'lg:top-7',
            )}
          >
            {aside}
          </div>
        ) : null}
      </div>
    </div>
  );
}
